import { useState, type FormEvent } from "react";
import { api, ApiError, type PasswordResetIssued } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/**
 * Tenant-admin panel: reset a member's password without relying on the
 * member having a recovery email on file — a real self-service path now
 * also exists (ForgotPassword.tsx, /v1/auth/forgot-password), but it only
 * works once the member has set one (MyAccountPanel.tsx); this stays the
 * fallback for anyone who has not, or when an admin would rather hand the
 * link over directly.
 */
export function PasswordResetPanel() {
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<PasswordResetIssued | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const csrfToken = readCsrfCookie(document.cookie);
      const result = await api.requestPasswordReset(csrfToken, username);
      setIssued(result);
      setUsername("");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetLink = issued
    ? `${window.location.origin}/reset-password?token=${encodeURIComponent(issued.token)}`
    : null;

  return (
    <section aria-labelledby="password-reset-heading" className="card">
      <h2 id="password-reset-heading">Reset a user&rsquo;s password</h2>
      <p className="card__note">
        Only for a member of your own tenant. Like invites, there is no email integration yet: relay
        this link to them yourself.
      </p>

      {issued && resetLink ? (
        <div className="invites__created" role="status">
          <p>Send this link:</p>
          <code className="invites__link">{resetLink}</code>
          <button type="button" onClick={() => setIssued(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <form className="invites__form" onSubmit={(event) => void handleSubmit(event)}>
        <label className="field">
          <span>Username</span>
          <input
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
        </label>
        <button type="submit" disabled={submitting}>
          {submitting ? "Requesting…" : "Reset password"}
        </button>
      </form>

      {error ? <p className="card__error">{error}</p> : null}
    </section>
  );
}
